import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { EdaData } from '../../types'; // Importiere stattdessen EdaData
// import '../../styles/Charts.css'; // Gemeinsame Chart-Styles wiederverwenden

// Hilfsfunktion für zufällige Farben (optional, zur besseren Unterscheidung)
const getRandomColor = (index: number): string => {
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#d0ed57', '#a4de6c', '#8dd1e1', '#83a6ed', '#84a6ed', '#9a8fea'];
  return colors[index % colors.length];
};

// Definieren eines spezifischeren Typs für die Feature-Statistik direkt hier
// Basierend auf der erwarteten Struktur aus EdaData und den Linter-Fehlern
type SingleFeatureStats = EdaData['eda_results']['features_stats']['stats_per_feature'][string];

interface CategoricalFeatureDistributionChartProps {
    featureName: string;
    featureStats: SingleFeatureStats; // Verwende den spezifischeren Typ
    totalSamples: number;
    allCategoricalFeatures?: string[]; // Liste aller verfügbaren kategorialen Features
    selectedFeature?: string;         // Das aktuell ausgewählte Feature (von App.tsx)
    onFeatureSelect?: (featureName: string) => void; // Callback für App.tsx
    showSelector?: boolean;           // Ob das Dropdown angezeigt werden soll
}

// Definieren des Typs für die Chart-Daten, um Typinferenzprobleme zu beheben
interface ChartDataItem {
    name: string;
    Anzahl: number;
    Prozent: number;
}

export const CategoricalFeatureDistributionChart: React.FC<CategoricalFeatureDistributionChartProps> = ({
    featureName, // Wird jetzt primär als initialer Wert oder bei Grid-Anzeige genutzt
    featureStats,
    totalSamples,
    allCategoricalFeatures = [], // Default leeres Array
    selectedFeature,
    onFeatureSelect,
    showSelector = false // Default: kein Selector anzeigen
}) => {
    // State für die *interne* Auswahl, falls die Komponente im Einzelmodus läuft
    const [internalSelectedFeature, setInternalSelectedFeature] = useState(selectedFeature || featureName);

    // Effekt, um den internen State zu aktualisieren, wenn sich die Props ändern (z.B. beim Laden neuer Daten)
    useEffect(() => {
        setInternalSelectedFeature(selectedFeature || featureName);
    }, [selectedFeature, featureName]);

    // Handler für das interne Dropdown
    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newFeature = event.target.value;
        setInternalSelectedFeature(newFeature);
        if (onFeatureSelect) {
            onFeatureSelect(newFeature); // Informiere App.tsx über die Auswahl
        }
    };

    // Logik, um die korrekten Stats zu verwenden (basierend auf interner oder externer Auswahl)
    // HINWEIS: featureStats wird aktuell noch von App.tsx für das *initial* ausgewählte Feature übergeben.
    // Im Idealfall würde die Komponente die Stats selbst laden, basierend auf `internalSelectedFeature`.
    // Für jetzt belassen wir es so, dass App.tsx die korrekten Stats basierend auf `selectedCategoricalFeature` übergibt.
    const currentFeatureName = showSelector ? internalSelectedFeature : featureName;

    // Sicherstellen, dass es sich um kategoriale Stats handelt und top_5_values existiert
    if (featureStats.dtype === 'object' || featureStats.dtype === 'string' || featureStats.dtype === 'category' || featureStats.dtype === 'bool') {
        // Explizite Typisierung für topValues
        const topValues: Record<string, number> = featureStats.top_5_values || {};
        const uniqueCount = featureStats.unique_values_count ?? 0;
        const missingCount = featureStats.missing_values ?? 0;

        let totalTop5Count = 0;
        // Explizite Typisierung für chartData
        const chartData: ChartDataItem[] = Object.entries(topValues)
            .map(([value, count]) => {
                 // Stelle sicher, dass count eine Zahl ist (obwohl es aus top_5_values kommen sollte)
                 const numericCount = typeof count === 'number' ? count : 0;
                 totalTop5Count += numericCount;
                return {
                    name: value === null || value === undefined || value === '' ? '(Leer)' : String(value),
                    Anzahl: numericCount, // Verwende die sichergestellte Zahl
                    Prozent: totalSamples > 0 ? parseFloat(((numericCount / totalSamples) * 100).toFixed(2)) : 0, // Prüfe totalSamples > 0
                };
            })
            .sort((a, b) => b.Anzahl - a.Anzahl); // Sortierung sollte jetzt funktionieren

        // Füge "Sonstige" hinzu, wenn es mehr als 5 eindeutige Werte gibt (oder wenn Top5 nicht alle abdeckt)
        // Berechne die Anzahl der "Sonstigen" genauer, indem wir die fehlenden Werte *nicht* mitzählen
        const nonMissingSamples = totalSamples - missingCount;
        const otherCount = nonMissingSamples - totalTop5Count;

        if (uniqueCount > 5 && otherCount > 0) {
             chartData.push({
                 name: 'Sonstige',
                 Anzahl: otherCount,
                 Prozent: totalSamples > 0 ? parseFloat(((otherCount / totalSamples) * 100).toFixed(2)) : 0, // Prüfe totalSamples > 0
             });
        }
         // Füge "Fehlend" hinzu, wenn es fehlende Werte gibt
        if (missingCount > 0) {
             chartData.push({
                 name: 'Fehlend',
                 Anzahl: missingCount,
                 Prozent: totalSamples > 0 ? parseFloat(((missingCount / totalSamples) * 100).toFixed(2)) : 0, // Prüfe totalSamples > 0
             });
        }

        // Zeige Nachricht, wenn keine Daten vorhanden sind (sollte nicht oft vorkommen)
         if (chartData.length === 0) {
            return (
                <div className="chart-container card">
                    <h4>Verteilung: {featureName}</h4>
                    <p>Keine darstellbaren Werte für dieses Feature gefunden.</p>
                </div>
            );
        }

        return (
            <div className="chart-container card">
                {/* Conditional Dropdown */}
                {showSelector && allCategoricalFeatures.length > 0 && (
                    <div className="chart-selector-container"> {/* Optional: Container für Styling */}
                        <label htmlFor={`cat-select-${featureName}`}>Kategorie auswählen:</label>
                        <select
                            id={`cat-select-${featureName}`} // Eindeutige ID
                            value={internalSelectedFeature}
                            onChange={handleSelectChange}
                        >
                            {allCategoricalFeatures.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <h4>Verteilung: {currentFeatureName} (Top {Math.min(5, uniqueCount)}{uniqueCount > 5 || missingCount > 0 ? '+ Sonstige/Fehlend' : ''})</h4>
                 <p className="chart-subtitle">Gesamte eindeutige Werte: {uniqueCount}</p>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        layout="vertical" // Vertikales Layout für bessere Lesbarkeit langer Namen
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 'dataMax + 5']} allowDecimals={false} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={100} // Mehr Platz für Namen
                            tick={{ fontSize: 10 }} // Kleinere Schriftgröße
                        />
                        <Tooltip
                            formatter={(value: number, name: string) => {
                                if (name === 'Anzahl') return [value, 'Anzahl'];
                                if (name === 'Prozent') return [`${value}%`, 'Prozent'];
                                return value;
                            }}
                            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', color: '#333' }} // Tooltip Styling
                        />
                        <Legend />
                        <Bar dataKey="Anzahl" name="Anzahl" fill="#8884d8">
                             {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getRandomColor(index)} />
                             ))}
                        </Bar>
                         {/* Optional: Prozent als zweite Leiste oder nur im Tooltip? Hier nur im Tooltip */}
                         {/* <Bar dataKey="Prozent" name="Prozent (%)" fill="#82ca9d" /> */}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // Fallback, falls es doch keine kategorialen Stats sind
    return null;
}; 